/**
 * Event search (Outlook/Google): a search box in the calendar bar; Enter lists the events of the shown calendars
 * whose subject or location matches (series once, newest first). A result jumps to its day and opens it.
 */
import { useI18n } from '@platform/i18n';
import { Button, CALENDAR_COLOR_CLASSES, cn, EmptyState, ErrorState, Input, Skeleton, useAsync, useErrorText } from '@platform/ui';
import { ArrowLeftIcon, RepeatIcon, SearchIcon } from 'lucide-react';
import { type FormEvent, type Ref } from 'react';
import { api } from '../api';
import { useCalendar } from './calendar-context';
import { calendarName, toShownEvents } from './events';
import { useCalendarFormat } from './format';

export function SearchBox({ id, inputRef, onSearch }: { id: string; inputRef?: Ref<HTMLInputElement>; onSearch?: (query: string) => void }) {
  const { t } = useI18n();
  const { searchText: text, setSearchText: setText, setSearchQuery } = useCalendar();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (text.trim().length >= 2) (onSearch ?? setSearchQuery)(text.trim());
  };
  return (
    <form role="search" onSubmit={submit} className="relative h-control w-full max-w-md">
      <SearchIcon aria-hidden className="pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input ref={inputRef} id={id} type="search" value={text} onChange={(e) => { setText(e.target.value); if (!e.target.value) setSearchQuery(undefined); }} placeholder={t('webmail.calendarUx.search.placeholder')} aria-label={t('webmail.calendarUx.search.label')} className="h-control w-full pl-8" />
    </form>
  );
}

export function SearchResults({ query, calendarIds, onClose }: { query: string; calendarIds: readonly string[]; onClose: () => void }) {
  const { t } = useI18n();
  const format = useCalendarFormat();
  const errorText = useErrorText();
  const { calendars, setDate, setSelectedId, zone } = useCalendar();
  const results = useAsync(() => api.searchEvents(calendarIds, query), `${query}|${calendarIds.join(',')}`);
  const shown = toShownEvents(results.data ?? [], calendars.data ?? [], (e) => e.subject || t('webmail.calendar.noTitle'), [], zone.toWall).sort((a, b) => b.grid.start.getTime() - a.grid.start.getTime());

  return (
    <section aria-label={t('webmail.calendarUx.search.results', { query })} className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          <ArrowLeftIcon />
          {t('webmail.calendarUx.search.back')}
        </Button>
        <h2 className="truncate text-sm font-semibold">{t('webmail.calendarUx.search.results', { query })}</h2>
      </div>
      {results.error ? (
        <ErrorState description={errorText.describe(results.error)} onRetry={results.reload} />
      ) : !results.data ? (
        <div className="flex flex-col gap-2 p-3" aria-busy="true">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : shown.length === 0 ? (
        <EmptyState title={t('webmail.calendarUx.search.empty')} description={t('webmail.calendarUx.search.emptyDescription')} />
      ) : (
        <ul className="min-h-0 flex-1 divide-y overflow-y-auto">
          {shown.map((s) => (
            <li key={s.grid.id}>
              <button
                type="button"
                onClick={() => {
                  setDate(s.grid.start);
                  setSelectedId(s.grid.id);
                  onClose();
                }}
                className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span aria-hidden className={cn('h-8 w-1 shrink-0 rounded-full', CALENDAR_COLOR_CLASSES[s.grid.color].swatch)} />
                <span className="w-40 shrink-0 text-xs text-muted-foreground max-md:w-24">
                  {format.shortDay(s.grid.start)}
                  <br />
                  {s.grid.allDay ? t('webmail.calendar.allDay') : format.range(s.grid.start, s.grid.end, false)}
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="flex items-center gap-1 truncate font-medium">
                    {s.grid.title}
                    {s.event.isRecurring && <RepeatIcon aria-hidden className="size-3 shrink-0" />}
                  </span>
                  {s.grid.detail && <span className="truncate text-xs text-muted-foreground">{s.grid.detail}</span>}
                </span>
                <span className="truncate text-xs text-muted-foreground max-md:hidden">{calendarName(s.calendar, t)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
